import * as moment from 'moment';
import { formatMoney } from '../../_helpers/functions';

export interface SummaryTableData {
  date: string;
  time: string;
  merchantId: string;
  firstName: string;
  lastName: string;
  txnType: string;
  salesType: string;
  status: 'success' | 'soft' | 'hard';
  response: string;
  txnId: string;
  orderId: string;
  platform: string;
  email: string;
  ccType: 'visa' | 'mastercard';
  amount: number;
  attemptedDate?: string;
}

export interface SummaryData {
  transaction_attempted_date: string;
  merchant_id: string;
  customer_first_name: string;
  customer_last_name: string;
  transaction_type: string;
  sales_type: string;
  transaction_status: string;
  transaction_id: string;
  customer_email: string;
  card_type: string;
  transaction_amount: number | string;
  three_ds_enabled: boolean;
  card_bin: string;
  card_last_four: string;
  order_id: string;
  platform: string;
}

export interface TransactionDetail {
  merchant_id: string;
  transaction_attempted_date: string;
  transaction_type: string;
  gateway_id: number;
  transaction_id: string;
  transaction_status: string;
  card_last_four: string;
  card_type: string;
  avs_code: string;
  processor_type: string;
  network_transaction_id: string;
  response_code: string;
  customer_first_name: string;
  customer_last_name: string;
  customer_email: string;
  billing_first_name: string;
  billing_last_name: string;
  billing_email: string;
  billing_phone: string;
  billing_address: string;
  shipping_first_name: string;
  shipping_last_name: string;
  shipping_email: string;
  shipping_phone: string;
  shipping_address: string;
  transaction_amount: number;
  reason: string | null;
  approval_code: string;
  ecommerce_indicator: string;
  terminal_number: string;
  raw_emv_tag: string;
  three_ds_enabled: boolean;
  order_id: string;
  ip_address: string;
  sales_type: string;
  is_rebill_transaction: boolean;
  cdn_installed: boolean;
  customer_vault_id: string;
  decline_source: string;
  payment_instrument_id: string;
  ach_verification_code: string;
  source_url: string;
  card_expiry_year: string;
  card_expiry_month: string;
  parent_transaction_id: string;
  nt_used: boolean;
  initiated_by: string;
  avs_code_value: string;
  three_ds_status: string;
  cvv_result_code_value: string;
  three_ds_server_transaction_id: string;
  store_indicator: string;
  three_ds_version: string;
  liability_shift: boolean;
  processor_response_text: string;
  gateway_response_text: string;
  action_history: TransactionActionHistory[];
}

export interface TransactionActionHistory {
  name: string;
  name_value: string;
  rCode: string;
  rFlag: string;
  rMessage: string;
  reasonCode: string;
  reconciliationId?: string;
  returnCode: number | string;
  status?: string;
  showDetails?: boolean;
}

export const SummaryTableFields = [
  'transaction_attempted_date',
  'merchant_id',
  'customer_first_name',
  'customer_last_name',
  'sales_type',
  'transaction_type',
  'transaction_status',
  'customer_email',
  'card_type',
  'transaction_amount',
  'three_ds_enabled',
  'card_bin',
  'card_last_four',
  'gateway_name',
  'platform',
];

export const TransactionDetailFields = [
  'merchant_id',
  'transaction_attempted_date',
  'transaction_type',
  'sales_type',
  'gateway_id',
  'transaction_id',
  'transaction_status',
  'card_last_four',
  'card_type',
  'avs_code',
  'processor_type',
  'response_code',
  'customer_first_name',
  'customer_last_name',
  'customer_email',
  'billing_first_name',
  'billing_last_name',
  'billing_email',
  'billing_phone',
  'billing_address',
  'shipping_first_name',
  'shipping_last_name',
  'shipping_phone',
  'shipping_email',
  'shipping_address',
  'transaction_amount',
  'reason',
  'approval_code',
  'ecommerce_indicator',
  'terminal_number',
  'raw_emv_tag',
  'three_ds_enabled',
  'order_id', // Reference Order ID
  'ip_address',
  'action_history',
  'is_rebill_transaction', // Rebill Txn
  'cdn_installed',
  'customer_vault_id',
  'decline_source',
  'payment_instrument_id', // being used for Vault ID
  'ach_verification_code',
  'source_url', // Referral URL
  'card_expiry_year',
  'card_expiry_month',
  'parent_transaction_id',
  'network_transaction_id',
  'nt_used',
  'initiated_by',
  'avs_code_value',
  'cvv_result_code_value',
  'three_ds_status',
  'three_ds_server_transaction_id',
  'store_indicator',
  'three_ds_version',
  'liability_shift',
  'processor_response_text',
  'gateway_response_text',
];

export enum CardType {
  Visa = 'Visa',
  MasterCard = 'Mastercard',
  Discover = 'Discover',
  Amex = 'American Express',
}

export const CardIconGreyMap: { [key in CardType]: string } = {
  [CardType.Visa]: '/assets/IconsLibrary/visa-icon-grey.svg',
  [CardType.MasterCard]: '/assets/IconsLibrary/mastercard-icon-grey.svg',
  [CardType.Discover]: '/assets/IconsLibrary/discover-icon-grey.svg',
  [CardType.Amex]: '/assets/IconsLibrary/amex-icon-grey.svg',
};

export const CardIconColorMap: { [key in CardType]: string } = {
  [CardType.Visa]: '/assets/IconsLibrary/visa-icon.svg',
  [CardType.MasterCard]: '/assets/IconsLibrary/mastercard-icon.svg',
  [CardType.Discover]: '/assets/IconsLibrary/discover-icon.svg',
  [CardType.Amex]: '/assets/IconsLibrary/amex-icon.svg',
};

export enum SalesType {
  INITIAL = 'initial',
  REBILL = 'rebill',
  ONE_TIME = 'one-time',
}

export enum TransactionType {
  SALE = 'sale',
  AUTHORIZE = 'authorize',
  CAPTURE = 'capture',
  VOID = 'void',
  REFUND = 'refund',
  CHARGEBACK = 'chargeback',
  ALERT = 'alert',
}

export function getTxnAmount(amount: string | number, txnType: string): string {
  let formattedAmount = '$' + formatMoney(amount);
  if (txnType === TransactionType.REFUND) {
    formattedAmount = `-${formattedAmount}`;
  }
  return formattedAmount;
}

export const DateRanges = {
  Today: [moment(), moment()],
  Yesterday: [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
  'Past 7 Days': [moment().subtract(7, 'days'), moment()],
  'Month to Date': [moment().startOf('month'), moment()],
  'Last Month': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
  'Last 30 days': [moment().subtract(30, 'days'), moment()],
  'Last 3 Months to Date': [moment().subtract(3, 'months').startOf('month'), moment()],
  'Last 6 Months to Date': [moment().subtract(6, 'months').startOf('month'), moment()],
  'Last 12 Months to Date': [moment().subtract(12, 'months').startOf('month'), moment()],
  'Year to Date': [moment().startOf('year'), moment()],
  'Last Year': [moment().subtract(1, 'year').startOf('year'), moment().subtract(1, 'year').endOf('year')],
};
